import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from '../../../shared/lib/supabase';
import { Molecule3DViewer } from '../../molecules/components/Molecule3DViewer';
import { convertJSONToPDB } from '../../molecules/utils/jsonToPdb';
import { useTabNavigation } from '../../../shared/hooks/useTabNavigation';
import { 
  Download, 
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Rocket,
  ArrowRight,
  Copy,
  Dna,
  Plus
} from "lucide-react";

interface OdysseyJob {
  job_id: string;
  name: string;  // Changed from job_name to name
  status: string;
  created_at: string;
  updated_at?: string;
  json_input?: any;
  json_output?: any;
  pdb_output?: string;
  cif_output?: string;
  metadata?: any;
  error?: string;
  processing_started_at?: string;
  processing_completed_at?: string;
  project_id?: string;
  generation_id?: number;
  user_id?: string;
  config?: any;
  current_stage?: string;
  stage_details?: string;
  workflow_type?: string;
}

interface ProteinMetadata {
  num_residues: number;
  num_masked: number;
  num_mutations: number;
  generated_sequence: string;
  original_sequence: string;
  processing_time_seconds: number;
  model_version: string;
}

interface JobDetailsProps {
  jobId?: string;
}

export function JobDetails({ jobId: propJobId }: JobDetailsProps = {}) {
  const { jobId: paramJobId } = useParams<{ jobId: string }>();
  const jobId = propJobId || paramJobId;
  const navigate = useNavigate();
  const [odysseyJob, setOdysseyJob] = useState<OdysseyJob | null>(null);
  const [metadata, setMetadata] = useState<ProteinMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdbContent, setPdbContent] = useState<string>('');
  const [jsonOutput, setJsonOutput] = useState<any>(null);
  const [isGeneratingNext, setIsGeneratingNext] = useState(false);
  const [showJsonViewer, setShowJsonViewer] = useState(false);
  const subscriptionRef = useRef<any>(null);

  useEffect(() => {
    if (!jobId) {
      if (!propJobId) {
        toast.error("No job ID provided");
        navigate('/');
      }
      return;
    }

    loadOdysseyJob();
    setupRealtimeSubscription();

    // Polling for incomplete jobs
    const pollInterval = setInterval(() => {
      if (odysseyJob && (odysseyJob.status === 'processing' || odysseyJob.status === 'pending')) {
        loadOdysseyJob();
      }
    }, 3000);

    return () => {
      clearInterval(pollInterval);
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, [jobId, odysseyJob?.status]);

  const loadOdysseyJob = async () => {
    if (!jobId) return;

    // Only set loading to true if we don't have a job yet (initial load)
    if (!odysseyJob) {
      setLoading(true);
    }

    try {
      // Query odyssey_jobs table
      const { data, error } = await supabase
        .from('odyssey_jobs')
        .select('*')
        .eq('job_id', jobId)
        .single();

      if (error) {
        console.error('Error loading Odyssey job:', error);
        toast.error('Failed to load job details');
        return;
      }

      setOdysseyJob(data);

      // Parse metadata if available
      if (data.metadata) {
        try {
          const parsedMetadata = typeof data.metadata === 'string' 
            ? JSON.parse(data.metadata) 
            : data.metadata;
          setMetadata(parsedMetadata);
        } catch (e) {
          console.error('Error parsing metadata:', e);
        }
      }

      // Parse JSON output if available
      if (data.json_output) {
        try {
          const parsed = typeof data.json_output === 'string'
            ? JSON.parse(data.json_output)
            : data.json_output;
          setJsonOutput(parsed);
        } catch (e) {
          console.error('Error parsing JSON output:', e);
        }
      }

      // Set PDB content if available
      if (data.pdb_output) {
        setPdbContent(data.pdb_output);
      } else if (data.json_output && !data.pdb_output && data.status === 'completed') {
        // Auto-convert JSON to PDB if not already done
        convertAndStorePDB(data);
      }
    } catch (error) {
      console.error('Error in loadOdysseyJob:', error);
      toast.error('Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const convertAndStorePDB = async (job: OdysseyJob) => {
    if (!job.json_output) return;

    try {
      const jsonData = typeof job.json_output === 'string'
        ? JSON.parse(job.json_output)
        : job.json_output;
      
      const pdbString = convertJSONToPDB(jsonData);
      setPdbContent(pdbString);

      // Store PDB in database
      const { error } = await supabase
        .from('odyssey_jobs')
        .update({ pdb_output: pdbString })
        .eq('job_id', job.job_id);

      if (error) {
        console.error('Error storing PDB:', error);
      }
    } catch (error) {
      console.error('Error converting JSON to PDB:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!jobId) return;

    const channel = supabase
      .channel(`odyssey-job-${jobId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'odyssey_jobs',
          filter: `job_id=eq.${jobId}`
        },
        (payload) => {
          console.log('Realtime update for Odyssey job:', payload);
          setOdysseyJob(payload.new as OdysseyJob);
          
          // Update metadata and outputs
          if (payload.new.metadata) {
            try {
              const parsedMetadata = typeof payload.new.metadata === 'string'
                ? JSON.parse(payload.new.metadata)
                : payload.new.metadata;
              setMetadata(parsedMetadata);
            } catch (e) {
              console.error('Error parsing realtime metadata:', e);
            }
          }

          if (payload.new.json_output) {
            try {
              const parsed = typeof payload.new.json_output === 'string'
                ? JSON.parse(payload.new.json_output)
                : payload.new.json_output;
              setJsonOutput(parsed);
            } catch (e) {
              console.error('Error parsing realtime JSON:', e);
            }
          }

          if (payload.new.pdb_output) {
            setPdbContent(payload.new.pdb_output);
          }
        }
      )
      .subscribe();

    subscriptionRef.current = channel;
  };

  const handleDownloadPDB = () => {
    if (!pdbContent) {
      toast.error('No PDB file available');
      return;
    }

    const blob = new Blob([pdbContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${odysseyJob?.name || 'odyssey'}_output.pdb`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadCIF = () => {
    if (!odysseyJob?.cif_output) {
      toast.error('No CIF file available');
      return;
    }

    const blob = new Blob([odysseyJob.cif_output], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${odysseyJob?.name || 'odyssey'}_output.cif`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRunNextGeneration = async () => {
    if (!odysseyJob || !pdbContent) {
      toast.error('No PDB output available for next generation');
      return;
    }

    setIsGeneratingNext(true);
    
    try {
      // Store PDB in molecule_viewer table for the Protein Design
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Authentication required');
        return;
      }

      // Create blob URL for PDB content
      const blob = new Blob([pdbContent], { type: 'text/plain' });
      const blobUrl = URL.createObjectURL(blob);

      // Generate unique storage path
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 9);
      const fileName = `odyssey_output_${timestamp}_${randomId}.pdb`;
      const storagePath = `${user.id}/${fileName}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('molecule-files')
        .upload(storagePath, blob, {
          contentType: 'text/plain',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        toast.error('Failed to prepare PDB for next generation');
        return;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('molecule-files')
        .getPublicUrl(storagePath);

      // Create entry in molecule_viewer table
      const { data: molecule, error: dbError } = await supabase
        .from('molecule_viewer')
        .insert({
          user_id: user.id,
          file_name: fileName,
          file_size: pdbContent.length,
          file_url: publicUrl,
          file_type: 'pdb',
          molecule_name: `Odyssey Output - ${odysseyJob.name}`,
          storage_path: storagePath,
          expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString() // 1 hour expiry
        })
        .select()
        .single();

      if (dbError) {
        console.error('Database error:', dbError);
        toast.error('Failed to create molecule entry');
        return;
      }

      // Navigate to molecule viewer with the uploaded PDB
      navigate(`/projects/${odysseyJob.project_id}/molecule/${molecule.id}`);
      toast.success('Opening Protein Design with Odyssey output');
      
    } catch (error) {
      console.error('Error preparing next generation:', error);
      toast.error('Failed to prepare next generation');
    } finally {
      setIsGeneratingNext(false);
    }
  };

  const getStatusIcon = () => {
    switch (odysseyJob?.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <Clock className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = () => {
    switch (odysseyJob?.status) {
      case 'completed':
        return 'text-green-600 bg-green-50';
      case 'processing':
        return 'text-blue-600 bg-blue-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Odyssey job...</p>
        </div>
      </div>
    );
  }

  if (!odysseyJob) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Job not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Rocket className="h-6 w-6 text-purple-600" />
              <h1 className="text-2xl font-semibold text-gray-900">
                {odysseyJob.name}
              </h1>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                {getStatusIcon()}
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}>
                  {odysseyJob.status}
                </span>
              </span>
              <span>Created: {new Date(odysseyJob.created_at).toLocaleString()}</span>
              {odysseyJob.processing_completed_at && (
                <span>Completed: {new Date(odysseyJob.processing_completed_at).toLocaleString()}</span>
              )}
            </div>
          </div>
          
          {odysseyJob.status === 'completed' && (
            <button
              onClick={handleRunNextGeneration}
              disabled={isGeneratingNext}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-md hover:from-purple-700 hover:to-purple-900 transition-all duration-300 shadow-lg disabled:opacity-50"
            >
              {isGeneratingNext ? (
                <>
                  <Clock className="h-4 w-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Run Next Generation
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {odysseyJob.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-900">Processing Error</h3>
              <p className="text-sm text-red-700 mt-1">{odysseyJob.error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {odysseyJob.status === 'completed' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 3D Viewer */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">3D Structure</h2>
              <div className="flex gap-2">
                {pdbContent && (
                  <button
                    onClick={handleDownloadPDB}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Download PDB
                  </button>
                )}
                {odysseyJob?.cif_output && (
                  <button
                    onClick={handleDownloadCIF}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Download CIF
                  </button>
                )}
              </div>
            </div>
            {pdbContent ? (
              <Molecule3DViewer pdbContent={pdbContent} height="h-96" />
            ) : (
              <div className="h-96 bg-gray-50 rounded-lg flex items-center justify-center">
                <p className="text-gray-500">No PDB output available</p>
              </div>
            )}
          </div>

          {/* Metadata & Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Generation Details</h2>
              <button
                onClick={() => setShowJsonViewer(!showJsonViewer)}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
              >
                <FileText className="h-4 w-4" />
                {showJsonViewer ? 'Hide' : 'View'} JSON
              </button>
            </div>
            
            {metadata ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Total Residues</p>
                    <p className="text-2xl font-semibold text-gray-900">{metadata.num_residues}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-sm text-purple-600">Masked Positions</p>
                    <p className="text-2xl font-semibold text-purple-900">{metadata.num_masked}</p>
                  </div>
                </div>
                
                {metadata.processing_time_seconds > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-600">Processing Time</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {Math.round(metadata.processing_time_seconds)} seconds
                    </p>
                  </div>
                )}
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600 mb-2">Generated Sequence</p>
                  <div className="font-mono text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                    {metadata.generated_sequence || 'N/A'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Dna className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>No metadata available</p>
              </div>
            )}
            
          </div>
        </div>
      )}

      {/* JSON Viewer Modal */}
      {showJsonViewer && jsonOutput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">JSON Output</h3>
              <button
                onClick={() => setShowJsonViewer(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(80vh-120px)]">
              <pre className="text-xs font-mono bg-gray-50 p-4 rounded-lg overflow-x-auto">
                {JSON.stringify(jsonOutput, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Processing Animation */}
      {odysseyJob.status === 'processing' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Rocket className="h-8 w-8 text-purple-600 animate-pulse" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Processing with Odyssey</h3>
            <p className="text-gray-600">This typically takes 15-50 minutes. You can safely leave this page.</p>
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}